import React, { useState, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import "./index.css";

const ReportSettings = ({ classrooms, users, firebase }) => {
  const [sendingPreviews, setSendingPreviews] = useState(false);
  const [sendingSingle, setSendingSingle] = useState({});
  const [localOverrides, setLocalOverrides] = useState({});
  const [frequencyOverrides, setFrequencyOverrides] = useState({});

  const classroomList = useMemo(() => {
    if (!classrooms) return [];

    return Object.entries(classrooms)
      .map(([classId, classData]) => {
        const isEnabled = classData.settings?.progressReportMail?.activated === true;
        const isClosed = !!classData.classroomClosed;
        const frequency = classData.settings?.progressReportMail?.frequency || "Not set";

        // Get coordinators from teachers array
        const teachers = classData.teachers ? Object.values(classData.teachers) : [];
        const coordinators = teachers
          .filter((t) => t.email)
          .map((t) => {
            let hasNotifPref = false;
            if (users) {
              const userEntry = Object.values(users).find(
                (u) => u.email?.toLowerCase() === t.email?.toLowerCase()
              );
              hasNotifPref = userEntry?.notificationPreferences?.progressReports === true;
            }
            return {
              name: t.name || t.displayName || "Unknown",
              email: t.email,
              hasProgressReportNotif: hasNotifPref,
            };
          });

        // Fallback: find mentors by classUID
        if (coordinators.length === 0 && users) {
          Object.values(users).forEach((u) => {
            if (u.isMentor && u.classUID === classId && u.email) {
              coordinators.push({
                name: u.displayName || u.username || "Unknown",
                email: u.email,
                hasProgressReportNotif:
                  u.notificationPreferences?.progressReports === true,
              });
            }
          });
        }

        // Compute next send date
        let nextSendDate = null;
        if (isEnabled && !isClosed) {
          const today = new Date();
          const dayOfMonth = today.getDate();
          if (frequency === "Weekly") {
            const next = new Date(today);
            next.setDate(next.getDate() + ((7 - next.getDay()) % 7 || 7));
            nextSendDate = next;
          } else if (frequency === "Every two weeks") {
            if (dayOfMonth < 15) {
              nextSendDate = new Date(today.getFullYear(), today.getMonth(), 15);
            } else {
              nextSendDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
            }
          } else if (frequency === "Monthly") {
            nextSendDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
          }
        }

        return {
          classId,
          className: classData.className || classId,
          isEnabled,
          isClosed,
          frequency,
          coordinators,
          nextSendDate,
        };
      })
      .sort((a, b) => {
        if (a.isClosed !== b.isClosed) return a.isClosed ? 1 : -1;
        if (a.isEnabled !== b.isEnabled) return a.isEnabled ? -1 : 1;
        return a.className.localeCompare(b.className);
      });
  }, [classrooms, users]);

  const enabledClassrooms = classroomList.filter(
    (c) =>
      !c.isClosed &&
      (c.isEnabled || localOverrides[c.classId] === true) &&
      localOverrides[c.classId] !== false
  );
  const disabledClassrooms = classroomList.filter(
    (c) =>
      !c.isClosed &&
      !c.isEnabled &&
      localOverrides[c.classId] !== true
  );
  const closedClassrooms = classroomList.filter((c) => c.isClosed);

  const handleToggle = useCallback(
    async (classId, currentlyEnabled) => {
      const newValue = !currentlyEnabled;
      try {
        await firebase
          .classroom(classId)
          .child("settings/progressReportMail/activated")
          .set(newValue);
        setLocalOverrides((prev) => ({ ...prev, [classId]: newValue }));
        toast.success(
          `Reports ${newValue ? "enabled" : "disabled"} for classroom`
        );
      } catch (err) {
        console.error("Error toggling report setting:", err);
        toast.error("Failed to update setting");
      }
    },
    [firebase]
  );

  const handleFrequencyChange = useCallback(
    async (classId, newFrequency) => {
      try {
        await firebase
          .classroom(classId)
          .child("settings/progressReportMail/frequency")
          .set(newFrequency);
        setFrequencyOverrides((prev) => ({ ...prev, [classId]: newFrequency }));
        toast.success(`Frequency updated to "${newFrequency}"`);
      } catch (err) {
        console.error("Error updating frequency:", err);
        toast.error("Failed to update frequency");
      }
    },
    [firebase]
  );

  const triggerPreviewReports = useCallback(async () => {
    setSendingPreviews(true);
    try {
      const apiUrl =
        process.env.NODE_ENV === "development"
          ? "http://localhost:5000/red-platform/us-central1"
          : "https://us-central1-red-platform.cloudfunctions.net";
      const res = await fetch(
        `${apiUrl}/sendPreviewProgressReports?forceAll=true`
      );
      const data = await res.json();
      if (data.success) {
        toast.success(
          `Preview reports sent: ${data.reportsSent} reports to nico@myreded.com`
        );
      } else {
        toast.error("Failed to send preview reports");
      }
    } catch (err) {
      console.error("Error triggering preview reports:", err);
      toast.error("Error triggering preview reports");
    }
    setSendingPreviews(false);
  }, []);

  const triggerSinglePreview = useCallback(async (classId, className) => {
    setSendingSingle((prev) => ({ ...prev, [classId]: true }));
    try {
      const apiUrl =
        process.env.NODE_ENV === "development"
          ? "http://localhost:5000/red-platform/us-central1"
          : "https://us-central1-red-platform.cloudfunctions.net";
      const res = await fetch(
        `${apiUrl}/sendPreviewProgressReports?forceAll=true&classId=${classId}`
      );
      const data = await res.json();
      if (data.success) {
        toast.success(`Preview sent for ${className} to nico@myreded.com`);
      } else {
        toast.error(`Failed to send preview for ${className}`);
      }
    } catch (err) {
      console.error("Error triggering single preview:", err);
      toast.error("Error sending preview report");
    }
    setSendingSingle((prev) => ({ ...prev, [classId]: false }));
  }, []);

  const formatDate = (date) => {
    if (!date) return "—";
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const renderCoordinators = (coordinators) => {
    if (coordinators.length === 0) {
      return (
        <span className="preview-reports__no-coordinators">
          No coordinators assigned
        </span>
      );
    }
    return (
      <div className="preview-reports__coordinators-list">
        {coordinators.map((c, i) => (
          <div key={i} className="preview-reports__coordinator">
            <span className="preview-reports__coordinator-name">{c.name}</span>
            <span className="preview-reports__coordinator-email">
              {c.email}
            </span>
            {c.hasProgressReportNotif ? (
              <span className="preview-reports__notif-badge preview-reports__notif-badge--on">
                Subscribed
              </span>
            ) : (
              <span className="preview-reports__notif-badge preview-reports__notif-badge--off">
                Not subscribed
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderClassroomRow = (c, showToggle = false) => {
    const effectiveEnabled =
      localOverrides[c.classId] !== undefined
        ? localOverrides[c.classId]
        : c.isEnabled;
    const effectiveFrequency =
      frequencyOverrides[c.classId] || c.frequency;

    return (
      <div key={c.classId} className="preview-reports__classroom-row">
        <div className="preview-reports__classroom-header">
          <div className="preview-reports__classroom-info">
            <span className="preview-reports__classroom-name">
              {c.className}
            </span>
            {effectiveEnabled && !c.isClosed ? (
              <select
                className="preview-reports__frequency-select"
                value={effectiveFrequency}
                onChange={(e) =>
                  handleFrequencyChange(c.classId, e.target.value)
                }
              >
                <option value="Weekly">Weekly</option>
                <option value="Every two weeks">Every two weeks</option>
                <option value="Monthly">Monthly</option>
              </select>
            ) : (
              <span className="preview-reports__frequency">
                {effectiveFrequency}
              </span>
            )}
            {c.nextSendDate && (
              <span className="preview-reports__next-send">
                Next: {formatDate(c.nextSendDate)}
              </span>
            )}
          </div>
          <div className="preview-reports__actions">
            {!c.isClosed && (
              <button
                className="preview-reports__preview-btn"
                onClick={() => triggerSinglePreview(c.classId, c.className)}
                disabled={sendingSingle[c.classId]}
              >
                {sendingSingle[c.classId] ? "Sending..." : "Preview"}
              </button>
            )}
            {showToggle && !c.isClosed && (
              <button
                className={`preview-reports__toggle-btn ${
                  effectiveEnabled
                    ? "preview-reports__toggle-btn--disable"
                    : "preview-reports__toggle-btn--enable"
                }`}
                onClick={() => handleToggle(c.classId, effectiveEnabled)}
              >
                {effectiveEnabled ? "Disable" : "Enable"}
              </button>
            )}
          </div>
        </div>
        {renderCoordinators(c.coordinators)}
      </div>
    );
  };

  return (
    <div className="preview-reports__container">
      <div className="preview-reports__toolbar">
        <div className="preview-reports__toolbar-info">
          <span className="preview-reports__toolbar-summary">
            {enabledClassrooms.length} classroom
            {enabledClassrooms.length !== 1 ? "s" : ""} with reports enabled
          </span>
          <span className="preview-reports__toolbar-note">
            Preview emails will be sent to <strong>nico@myreded.com</strong>
          </span>
        </div>
        <button
          className="preview-reports__send-btn"
          onClick={triggerPreviewReports}
          disabled={sendingPreviews || enabledClassrooms.length === 0}
        >
          {sendingPreviews
            ? "Sending..."
            : `Preview All Reports (${enabledClassrooms.length})`}
        </button>
      </div>

      {/* Enabled Classrooms */}
      <div className="preview-reports__section">
        <div className="preview-reports__section-header preview-reports__section-header--enabled">
          <span>Reports Enabled</span>
          <span className="preview-reports__section-count">
            {enabledClassrooms.length}
          </span>
        </div>
        {enabledClassrooms.length === 0 ? (
          <p className="preview-reports__empty">
            No classrooms have reports enabled
          </p>
        ) : (
          enabledClassrooms.map((c) => renderClassroomRow(c, true))
        )}
      </div>

      {/* Disabled Classrooms */}
      <div className="preview-reports__section">
        <div className="preview-reports__section-header preview-reports__section-header--disabled">
          <span>Reports Disabled</span>
          <span className="preview-reports__section-count">
            {disabledClassrooms.length}
          </span>
        </div>
        {disabledClassrooms.length === 0 ? (
          <p className="preview-reports__empty">
            All active classrooms have reports enabled
          </p>
        ) : (
          disabledClassrooms.map((c) => renderClassroomRow(c, true))
        )}
      </div>

      {/* Closed Classrooms */}
      {closedClassrooms.length > 0 && (
        <div className="preview-reports__section">
          <div className="preview-reports__section-header preview-reports__section-header--closed">
            <span>Closed Classrooms</span>
            <span className="preview-reports__section-count">
              {closedClassrooms.length}
            </span>
          </div>
          {closedClassrooms.map((c) => renderClassroomRow(c, false))}
        </div>
      )}
    </div>
  );
};

export default ReportSettings;
